// Vendor imports
const classNames = require('classnames')
const PropTypes = require('prop-types')
const React = require('react')
// Shared imports
const BemEntity = require('shared/bem')
// Client V1 imports
const airbrake = require('client/lib/airbrake')
// Client V2 common imports
const tag = require('client-v2/common/utils/hyperscript')
const clientV1Bridge = require('client-v2/common/client-v1-bridge')
const { CapsuleButton } = require('client-v2/common/components/buttons')
const DropZone = require('client-v2/common/components/drop-zone')
const Modal = require('client-v2/common/components/modal')
// Container imports
const UploadImageModalDropIndicator = require('./components/drop-indicator')
const UploadImageModalPlaceholder = require('./components/placeholder')
const UploadImageModalPreview = require('./components/preview')
const { processFilePreviewState } = require('./utils')

const bem = BemEntity('UploadImageModal')

class UploadImageModal extends React.PureComponent {
  constructor(props) {
    super(props)

    this.state = {
      crop: null,
      dragging: false,
      error: null,
      file: null,
      image: null
    }

    // Bind event listeners
    this.onCancel = this.onCancel.bind(this)
    this.onCropped = this.onCropped.bind(this)
    this.onDrag = this.onDrag.bind(this)
    this.onDrop = this.onDrop.bind(this)
    this.onSubmit = this.onSubmit.bind(this)
  }

  // React lifecycle methods

  componentDidMount() {
    if (this.props.file) {
      this.setState(() => ({ file: this.props.file }))
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.isVisible && !this.props.isVisible) {
      this.setState(() => ({ file: null }))
    }
    if (prevProps.file !== this.props.file) {
      this.setState(() => ({ file: this.props.file }))
    }
    if (prevState.file !== this.state.file) {
      this.onFileChange()
    }
  }

  // Event handler methods

  onCancel() {
    if (this.state.image) {
      this.setState(() => ({ file: null }))
    } else {
      this.props.onDismiss()
    }
  }

  onCropped(crop) {
    this.setState(() => ({ crop }))
  }

  onDrag(dragging) {
    this.setState(() => ({ dragging }))
  }

  onDrop(file) {
    this.setState(() => ({ file }))
  }

  onFileChange() {
    processFilePreviewState(this.props, this.state.file)
    .then(previewState => {
      this.setState(() => previewState)
    })
    .catch(err => {
      this.setState(() => ({ file: null }))
      clientV1Bridge.showAlertModal({
        heading: 'Image processing failed',
        subheading: 'We could not use that image. Please try another.'
      })
      airbrake.notify(err)
    })
  }

  onSubmit() {
    const { image, crop } = this.state
    this.props.onSubmit({ image, crop })
  }

  // Button render methods

  renderCancelButton() {
    return tag(CapsuleButton, {
      className: bem.element('cancel-button'),
      onClick: this.onCancel
    },
      'Cancel'
    )
  }

  renderDeleteButton() {
    if (!this.props.savedImageUrl || this.state.image) {
      return null
    }
    return tag(CapsuleButton, {
      className: bem.element('delete-button'),
      onClick: this.props.onDelete
    },
      'Delete'
    )
  }

  renderSubmitButton() {
    if (!this.state.image || !this.state.crop) {
      return null
    }
    return tag(CapsuleButton, {
      className: bem.element('submit-button'),
      onClick: this.onSubmit
    },
      'Upload'
    )
  }

  // Component render methods

  renderDropIndicator() {
    return tag(UploadImageModalDropIndicator, {
      className: bem.element('drop-indicator'),
      isVisible: this.state.dragging
    })
  }

  renderError() {
    if (!this.state.error) {
      return null
    }
    return tag('div', {
      className: bem.element('error')
    },
      this.state.error.message
    )
  }

  renderFooter() {
    return tag('footer', {
      className: bem.element('footer')
    },
      this.renderDeleteButton(),
      this.renderCancelButton(),
      this.renderSubmitButton()
    )
  }

  renderHeader() {
    return tag('header', {
      className: bem.element('header')
    },
      this.props.headerText
    )
  }

  renderPlaceholder() {
    if (this.state.image) {
      return null
    }
    return tag(UploadImageModalPlaceholder, {
      className: bem.element('placeholder'),
      imagePurpose: this.props.imagePurpose,
      imageUrl: this.props.savedImageUrl,
      tips: this.props.tips,
      // Callbacks
      onFileSelect: this.onDrop
    })
  }

  renderPreview() {
    if (!this.state.image) {
      return null
    }
    return tag(UploadImageModalPreview, {
      className: bem.element('preview'),
      image: this.state.image,
      minHeight: this.props.minHeight,
      minWidth: this.props.minWidth,
      resizeHeight: this.props.resizeHeight,
      resizeWidth: this.props.resizeWidth,
      // Callbacks
      onCropped: this.onCropped
    })
  }

  // Root render method

  render() {
    if (!this.props.isVisible) {
      return null
    }
    const className = classNames(
      this.props.className,
      bem.block()
    )
    const wrapperElement = tag(DropZone, {
      onActive: this.onDrag,
      onDrop: this.onDrop
    })
    return tag(Modal, {
      className,
      wrapperElement,
      onDismiss: this.props.onDismiss
    },
      this.renderHeader(),
      this.renderPlaceholder(),
      this.renderPreview(),
      this.renderError(),
      this.renderFooter(),
      this.renderDropIndicator()
    )
  }
}

const filePropType = typeof File === 'function'
? PropTypes.instanceOf(File)
: PropTypes.shape({
  name: PropTypes.string,
  size: PropTypes.number,
  type: PropTypes.string
})

UploadImageModal.propTypes = {
  // Flags
  isVisible: PropTypes.bool,
  // Values
  className: PropTypes.string,
  file: filePropType,
  headerText: PropTypes.string,
  imagePurpose: PropTypes.string,
  minHeight: PropTypes.number,
  minWidth: PropTypes.number,
  resizeHeight: PropTypes.number,
  resizeWidth: PropTypes.number,
  savedImageUrl: PropTypes.string,
  supportedTypes: PropTypes.arrayOf(PropTypes.string),
  tips: PropTypes.arrayOf(PropTypes.string),
  // Callbacks
  onDismiss: PropTypes.func,
  onDelete: PropTypes.func,
  onSubmit: PropTypes.func
}

UploadImageModal.defaultProps = {
  headerText: 'Upload an image'
}

module.exports = UploadImageModal
